import { Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import React, { useRef } from "react";
import { TbChevronLeft, TbChevronRight } from "react-icons/tb";

import theme from "theme/themeThree";
import { EmojiSelector } from "MVVM/Components/EmoijSelector";
import { BaseSection } from "../styledComponents";
import { TLoadout } from "MVVM/Views/CreateMontage/@types";
import { InputText } from "MVVM/Components/Inputs";
import { getMomentGameName } from "MVVM/Utilities/Maps";
import { QuickEditPreferredLoadout } from "MVVM/State/quickEdit";
import { analytics } from "MVVM/Analytics";
import { IQuickEditMoment } from "../@types";
import { TQuickEditType, TQuickEditFlow } from "..";

interface ITitleAndLoadoutsProps {
  moment: IQuickEditMoment;
  selectedLoadout: TLoadout | null;
  setSelectedLoadout: (loadout: TLoadout) => void;
  title: string;
  setTitle: (title: string) => void;
  loadouts: TLoadout[];
  type: TQuickEditType;
  onCustomLoadoutClick?: () => void;
  onLoadoutChange?: () => void;
  section?: "title" | "loadout";
  flow?: TQuickEditFlow;
}

export const TitleAndLoadouts = ({
  moment,
  selectedLoadout,
  setSelectedLoadout,
  title,
  setTitle,
  loadouts,
  type,
  onCustomLoadoutClick,
  onLoadoutChange,
  section,
  flow,
}: ITitleAndLoadoutsProps) => {
  const history = useHistory();
  const setPreferredLoadout = useSetRecoilState(QuickEditPreferredLoadout);

  const handleEmojiSelect = (emoji: string) => {
    setTitle(title + emoji);
  };

  const stripRef = useRef<HTMLDivElement>(null);
  const initialLoadoutRef = useRef<TLoadout | null>(selectedLoadout);

  const initialLoadout = initialLoadoutRef.current;
  const otherLoadouts = [
    StudioLoadout,
    SurpriseMeLoadout,
    ...(loadouts ?? []),
  ].filter((l) => l.name !== initialLoadout?.name);

  const allLoadouts: TLoadout[] = [
    NoLoadout,
    ...(initialLoadout && initialLoadout.name !== NoLoadout.name
      ? [initialLoadout]
      : []),
    ...otherLoadouts,
  ];

  const handleLoadoutSelect = (loadout: TLoadout) => {
    if (onLoadoutChange) {
      onLoadoutChange();
      return;
    }
    setSelectedLoadout(loadout);
    setPreferredLoadout(loadout);
  };

  const scrollStrip = (direction: "left" | "right") => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === "right" ? 240 : -240,
      behavior: "smooth",
    });
  };

  const showTitle = !section || section === "title";
  const showLoadout = !section || section === "loadout";

  return (
    <>
      {showTitle && (
        <TitleSection>
          <Text
            variant="text.labelSmall"
            style={{
              width: "100%",
              justifyContent: "start",
              color: theme.colors.chalk,
            }}
          >
            Add a Title:
          </Text>
          <TitleInputContainer>
            <StyledInput
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
              placeholder="Enter a custom clip title, or we'll create one for you."
            />
            <EmojiButton>
              <EmojiSelector
                onEmojiSelect={handleEmojiSelect}
                position="bottom"
              />
            </EmojiButton>
          </TitleInputContainer>
        </TitleSection>
      )}
      {showLoadout && (
        <LoadoutSection>
          <LoadoutStripWrapper>
            <LoadoutStripArrow onClick={() => scrollStrip("left")}>
              <TbChevronLeft size={16} />
            </LoadoutStripArrow>
            <LoadoutStrip ref={stripRef}>
              {allLoadouts.map((loadout) => {
                const isSelected = selectedLoadout?.name === loadout.name;
                return (
                  <LoadoutCard
                    key={loadout.name}
                    $isSelected={isSelected}
                    onClick={() => handleLoadoutSelect(loadout)}
                  >
                    <LoadoutCardImage
                      src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/${loadout.image}`}
                      alt={loadout.name}
                    />
                    <LoadoutCardName $isSelected={isSelected}>
                      {loadout.name}
                    </LoadoutCardName>
                  </LoadoutCard>
                );
              })}
            </LoadoutStrip>
            <LoadoutStripArrow onClick={() => scrollStrip("right")}>
              <TbChevronRight size={16} />
            </LoadoutStripArrow>
          </LoadoutStripWrapper>
          {type === "MOMENT" && flow !== "time-remap" && (
            <CustomLoadoutLink
              onClick={() => {
                if (onCustomLoadoutClick) {
                  onCustomLoadoutClick();
                  return;
                }
                analytics.track("Quick Edit Select Custom Loadout", {});
                history.push(
                  `/remix/${moment.id}?game=["${getMomentGameName(
                    moment.game,
                  )}"]&next=${window.location.pathname}`,
                );
              }}
            >
              or create your own loadout
            </CustomLoadoutLink>
          )}
        </LoadoutSection>
      )}
    </>
  );
};

const StyledInput = styled(InputText)`
  ${theme.text.labelSmall}

  ::placeholder {
    color: ${(props) => props.theme.colors.chalkAlpha40};
  }
  background-color: ${(props) => props.theme.colors.grey825};
  border: none;
  flex: 1;
  padding-right: 40px;
`;

const TitleInputContainer = styled(Flex)`
  align-items: center;
  position: relative;
  width: 100%;
`;

const EmojiButton = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  position: absolute;
  right: 12px;
  z-index: 1;
`;

const TitleSection = styled(BaseSection)`
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  width: 100%;
  padding: 24px;
`;

const LoadoutSection = styled(Flex)`
  align-items: start;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  padding: 8px 0;
  width: 100%;
`;

const LoadoutStripWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
  width: 100%;
`;

const LoadoutStripArrow = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  padding: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const LoadoutStrip = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0;
  scroll-behavior: smooth;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const LoadoutCard = styled.div<{ $isSelected: boolean }>`
  border: 2px solid
    ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.envy : "transparent"};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
  transition: border-color 150ms ease;
  width: 100px;

  &:hover {
    border-color: ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.envy : theme.colors.chalkAlpha40};
  }
`;

const LoadoutCardImage = styled.img`
  display: block;
  height: 64px;
  object-fit: cover;
  width: 100%;
`;

const LoadoutCardName = styled.span<{ $isSelected: boolean }>`
  background: ${({ theme }) => theme.colors.darkerNed};
  color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.ice : theme.colors.chalkAlpha60};
  font-family: Barlow, sans-serif;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  padding: 6px 8px;
  text-align: center;
`;
const StudioLoadout = {
  name: "Use My Studio Cards",
  image: "web/signatures/envyLoadout.png",
} as TLoadout;

export const NoLoadout = {
  name: "No Loadout",
  image: "web/signatures/no_loadouts3.png",
} as TLoadout;

const SurpriseMeLoadout = {
  name: "Surprise Me",
  image: "web/signatures/random.png",
} as TLoadout;

const CustomLoadoutLink = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  cursor: pointer;
  font-size: 15px;
  transition: color 150ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;
